/**
 * Pixlie doodle pack — original monoline / primitivism line-art.
 * Inspired by hipster editorial monoline vibes (Claude/Anthropic-adjacent),
 * but NOT traced or copied from Anthropic assets.
 *
 * Use for landing carousel accents + mini icons in app chrome.
 */
import React from 'react';

export type DoodleName =
  | 'dolly'
  | 'faceLock'
  | 'waveform'
  | 'frames'
  | 'lens'
  | 'thread'
  | 'sparkCut'
  | 'promptReel';

export type DoodleSize = 'lg' | 'md' | 'sm' | 'nav' | 'mini';

const SIZE_PX: Record<DoodleSize, number> = {
  lg: 132,
  md: 68,
  sm: 38,
  /** Sidebar collapsed / rail icons — readable without labels */
  nav: 26,
  mini: 18,
};

const STROKE: Record<DoodleSize, number> = {
  lg: 2.4,
  md: 3.4,
  sm: 5,
  nav: 9.5,
  mini: 7.5,
};

interface PixlieDoodleProps {
  name: DoodleName;
  size?: DoodleSize;
  className?: string;
  title?: string;
}

/** Slightly wobbly paths — intentional hand-drawn feel */
function DoodlePaths({ name }: { name: DoodleName }) {
  switch (name) {
    case 'dolly':
      // Camera dolly: curved track + node beads + tiny camera block
      return (
        <g>
          <path d="M14 78 C 36 52, 58 94, 86 60 S 130 38, 148 54" />
          <circle cx="28" cy="70" r="3.2" />
          <circle cx="62" cy="74" r="3.2" />
          <circle cx="98" cy="58" r="3.2" />
          <circle cx="136" cy="50" r="3.2" />
          <path d="M118 34 h22 v16 h-14 l-8-6 z" />
          <circle cx="134" cy="42" r="3.5" />
          <path d="M24 88 q 8 -6 16 0" />
        </g>
      );
    case 'faceLock':
      // Soft face oval + corner brackets (identity lock) — not a scan UI clone
      return (
        <g>
          <path d="M58 30 C 42 38, 36 62, 44 86 C 52 108, 78 118, 98 112 C 120 104, 130 78, 124 54 C 118 34, 90 22, 58 30 Z" />
          <path d="M70 62 q 6 4 0 8" />
          <path d="M98 62 q 6 4 0 8" />
          <path d="M74 88 q 16 10 32 0" />
          <path d="M34 28 v-10 h10" />
          <path d="M126 28 v-10 h-10" />
          <path d="M34 118 v10 h10" />
          <path d="M126 118 v10 h-10" />
        </g>
      );
    case 'waveform':
      // Audio reactive: imperfect wave + beat dots
      return (
        <g>
          <path d="M18 80 C 30 40, 42 120, 54 70 S 78 30, 90 86 S 114 130, 126 64 S 146 50, 154 78" />
          <circle cx="42" cy="78" r="2.8" />
          <circle cx="78" cy="64" r="2.8" />
          <circle cx="118" cy="88" r="2.8" />
          <path d="M26 112 h112" opacity="0.45" />
        </g>
      );
    case 'frames':
      // Overlapping shot frames (v2v / restyle)
      return (
        <g>
          <rect x="28" y="36" width="74" height="54" rx="8" />
          <rect x="58" y="58" width="74" height="54" rx="8" />
          <path d="M48 52 h34" />
          <path d="M78 74 h34" />
          <circle cx="118" cy="78" r="3" />
        </g>
      );
    case 'lens':
      // Imperfect lens / iris with spark ticks
      return (
        <g>
          <ellipse cx="84" cy="80" rx="46" ry="44" />
          <ellipse cx="84" cy="80" rx="28" ry="26" />
          <circle cx="84" cy="80" r="10" />
          <path d="M84 28 v10" />
          <path d="M84 122 v10" />
          <path d="M30 80 h10" />
          <path d="M128 80 h10" />
          <path d="M48 48 l7 7" />
          <path d="M112 104 l7 7" />
        </g>
      );
    case 'thread':
      // Continuity thread through beads
      return (
        <g>
          <path d="M22 92 C 48 40, 70 130, 96 70 S 130 36, 150 78" />
          <circle cx="40" cy="78" r="5" />
          <circle cx="72" cy="96" r="5" />
          <circle cx="108" cy="68" r="5" />
          <circle cx="138" cy="74" r="5" />
          <path d="M148 58 l8 -10" />
          <path d="M152 62 l12 -2" />
        </g>
      );
    case 'sparkCut':
      // Cinema cut: angled slash + soft sparks (not Anthropic bolt)
      return (
        <g>
          <path d="M52 118 L108 38" />
          <path d="M64 44 l-8 -14" />
          <path d="M74 38 l2 -12" />
          <path d="M86 48 l10 -10" />
          <path d="M108 98 l12 8" />
          <path d="M98 110 l6 12" />
          <circle cx="58" cy="108" r="3" />
          <circle cx="114" cy="46" r="3" />
        </g>
      );
    case 'promptReel':
      // Prompt → reel: text line into film sprocket
      return (
        <g>
          <path d="M24 56 h58" />
          <path d="M24 72 h46" />
          <path d="M24 88 h52" />
          <path d="M86 72 C 102 72, 108 58, 124 58 h18 v48 h-18 c-16 0 -22 -14 -38 -14" />
          <rect x="124" y="58" width="22" height="48" rx="3" />
          <circle cx="131" cy="70" r="2.2" />
          <circle cx="139" cy="70" r="2.2" />
          <circle cx="131" cy="94" r="2.2" />
          <circle cx="139" cy="94" r="2.2" />
        </g>
      );
    default: {
      const _exhaustive: never = name;
      return _exhaustive;
    }
  }
}

export const PixlieDoodle: React.FC<PixlieDoodleProps> = ({
  name,
  size = 'md',
  className = '',
  title,
}) => {
  const px = SIZE_PX[size];
  const stroke = STROKE[size];
  return (
    <svg
      width={px}
      height={px}
      viewBox="0 0 168 160"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      role={title ? 'img' : 'presentation'}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <DoodlePaths name={name} />
    </svg>
  );
};

/** Map carousel / feature labels → doodle keys */
export const DOODLE_BY_LABEL: Record<string, DoodleName> = {
  'Cinema cut': 'sparkCut',
  'Style transfer': 'frames',
  'Audio reactive': 'waveform',
  'Identity lock': 'faceLock',
};

export const PIXLIE_DOODLE_NAMES: DoodleName[] = [
  'dolly',
  'faceLock',
  'waveform',
  'frames',
  'lens',
  'thread',
  'sparkCut',
  'promptReel',
];

export default PixlieDoodle;
